"use client"; // Ensure this runs only on client side

import { initializeApp, getApps, getApp, FirebaseApp, FirebaseError } from "firebase/app";
import { getAuth, Auth, onAuthStateChanged, User } from "firebase/auth";

console.log("Firebase module loading");

// Fallback/dummy values to use when API key is invalid
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyBoHYtEHwqRzVhYOKOY2N1kqMokvreej34",
  authDomain: "lab-atlas-460202.firebaseapp.com",
  projectId: "lab-atlas-460202",
  storageBucket: "lab-atlas-460202.appspot.com",
  messagingSenderId: "856840720986",
  appId: "1:856840720986:web:e60b684afc8df1a13f3e63",
  measurementId: "G-LM10FPE2GL"
};

// Create a mock Firebase auth to use when real Firebase fails to initialize
// This will allow the app to run without crashing, but auth functionality won't work
class MockFirebaseAuth implements Partial<Auth> {
  currentUser: User | null = null;
  
  // Basic implementation of onAuthStateChanged that just calls the observer with null
  onAuthStateChanged(nextOrObserver: any, error?: any, completed?: any) {
    console.log("Mock Auth: onAuthStateChanged called");
    if (typeof nextOrObserver === 'function') {
      nextOrObserver(null);
    } else if (nextOrObserver && typeof nextOrObserver.next === 'function') {
      nextOrObserver.next(null);
    }
    return () => {}; // Return unsubscribe function
  }
}

// Initialize Firebase
let firebaseApp: FirebaseApp;
let firebaseAuth: Auth;
let usingMockAuth = false;

try {
  // For development - log potential issues
  // Extract process.env values for logging (but don't use them directly)
  for (const key of [
    'NEXT_PUBLIC_FIREBASE_API_KEY',
    'NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN',
    'NEXT_PUBLIC_FIREBASE_PROJECT_ID',
    'NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET',
    'NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID',
    'NEXT_PUBLIC_FIREBASE_APP_ID',
    'NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID'
  ]) {
    const value = process.env[key] || '';
    console.log(`ENV: ${key} = ${value ? (value.length > 5 ? `${value.substring(0, 5)}...` : value) : 'undefined'}`);
  }
  
  // Check if any Firebase apps are initialized
  const existingApps = getApps();
  console.log(`Existing Firebase apps: ${existingApps.length}`);
  
  // Initialize Firebase app with hardcoded config
  if (!existingApps.length) {
    console.log("Initializing new Firebase app");
    
    // Use hardcoded values for reliability
    firebaseApp = initializeApp(FIREBASE_CONFIG);
    console.log("Firebase app initialization successful");
    
    // Print some config info
    console.log("Firebase config check:", {
      apiKey: FIREBASE_CONFIG.apiKey ? `${FIREBASE_CONFIG.apiKey.substring(0, 5)}... (length: ${FIREBASE_CONFIG.apiKey.length})` : "missing",
      authDomain: FIREBASE_CONFIG.authDomain,
      projectId: FIREBASE_CONFIG.projectId,
      storageBucket: FIREBASE_CONFIG.storageBucket
    });
  } else {
    console.log("Using existing Firebase app");
    firebaseApp = getApp();
  }
  
  // Get Auth instance
  firebaseAuth = getAuth(firebaseApp);
  console.log("Firebase auth initialized:", !!firebaseAuth);
  
  // Test auth functionality - don't actually call any methods
  if (typeof firebaseAuth.onAuthStateChanged !== 'function') {
    throw new Error('onAuthStateChanged is not a function');
  }
} catch (error) {
  console.error("Firebase initialization error:", error);
  
  if (error instanceof FirebaseError) {
    console.error(`Firebase Error Code: ${error.code}`);
    console.error(`Firebase Error Message: ${error.message}`);
    
    if (error.code === 'auth/invalid-api-key' || 
        error.code === 'auth/api-key-not-valid.-please-pass-a-valid-api-key.' ||
        error.message.includes('invalid-api-key') || 
        error.message.includes('api-key-not-valid')) {
      console.error("API KEY ERROR: The Firebase API key is invalid or not configured correctly");
      console.error("The app will run in development mode, but authentication functionality will not be available");
    } else if (error.code === 'auth/configuration-not-found' ||
               error.message.includes('configuration-not-found')) {
      console.error("FIREBASE AUTH ERROR: Authentication configuration not found");
      console.error("This typically happens when Firebase Authentication is not enabled in the Firebase Console");
      console.error("Please go to the Firebase Console, navigate to Authentication, and click 'Get Started' to enable it");
      console.error("You may also need to enable at least one sign-in method (like Email/Password)");
    }
  }
  
  // Initialize with mock implementations to prevent null reference errors
  // This allows the app to render, but auth functionality won't work
  console.log("Using mock Firebase implementation for graceful fallback");
  firebaseApp = {} as FirebaseApp;
  firebaseAuth = new MockFirebaseAuth() as unknown as Auth;
  usingMockAuth = true;
}

// Export Firebase instances
export const app = firebaseApp;
export const auth = firebaseAuth;
export const isUsingMockAuth = usingMockAuth; 
